import { Component, OnDestroy, ViewChild } from '@angular/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { catchError, EMPTY, finalize, of, switchMap, tap, Observable, Subscription } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CreateTemplateVersionResponse, TemplateRequest, UploadUrlResponse } from '@workspaces/models/templates';
import { WorkspaceTemplateService } from '@workspaces/services/templates.service';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { Router, RouterModule } from '@angular/router';
import { TemplateFormComponent } from '../template-form/template-form.component';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-template-create',
  templateUrl: './template-create.component.html',
  styleUrls: ['./template-create.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [CommonModule, MatTabsModule, RouterModule, MatIcon, MatSnackBarModule, TemplateFormComponent]
})
export class TemplateCreateComponent implements OnDestroy {
  @ViewChild(TemplateFormComponent) templateForm!: TemplateFormComponent;
  private readonly sub = new Subscription();
  public loading = false;

  private createdTemplateInformations: CreateTemplateVersionResponse | null = null;

  constructor(
    private readonly templateService: WorkspaceTemplateService,
    private readonly http: HttpClient,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router
  ) {}

  onSubmit(event: { form: TemplateRequest; file: File | null }): void {
    this.loading = true;

    const process$ = this.createTemplate(event.form).pipe(
      switchMap(template => this.handleOptionalFileUpload(template, event.file)),
      tap(() => this.onSuccess()),
      catchError(() => this.handleError()),
      finalize(() => {
        this.loading = false;

        if (this.createdTemplateInformations) {
          this.router.navigate(['/workspaces/template', this.createdTemplateInformations.id], { replaceUrl: true });
        }
      })
    );

    this.sub.add(process$.subscribe());
  }

  private createTemplate(payload: TemplateRequest): Observable<CreateTemplateVersionResponse> {
    return this.templateService.createTemplate(payload).pipe(
      tap(template => {
        this.createdTemplateInformations = template;
      })
    );
  }

  private handleOptionalFileUpload(template: CreateTemplateVersionResponse, file: File | null) {
    if (!file) {
      return of(template);
    }

    const extension = file.name.split('.').pop() ?? '';
    return this.templateService
      .generateUploadUrl(template.id, template.version, extension)
      .pipe(switchMap((uploadRes: UploadUrlResponse) => this.uploadToS3(uploadRes.url, file)));
  }

  private uploadToS3(url: string, file: File) {
    const headers = new HttpHeaders({ 'Content-Type': file.type });
    return this.http.put(url, file, { headers });
  }

  private onSuccess(): void {
    this.loading = false;
    this.snackBar.open('Template created successfully!', 'Dismiss', { duration: 3000 });
    this.templateForm.reset();
  }

  private handleError(): Observable<never> {
    this.loading = false;
    this.snackBar.open('Error during template creation', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
    return EMPTY;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
